import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { PremiumService, TrialOffer } from '../../@services/front/premium.service';
import { PricingService } from '../../@services/front/pricing.service';
import { CheckoutPlan } from '../../@services/front/checkout.service';
import { CheckoutProviderService } from '../../@services/front/checkout-provider.service';
import { ChevronRightIconComponent } from '../../../@icons/chevron-right.component';

/**
 * Carte d'offre Premium, en ligne de réglage comme la ligne « Premium » des
 * apps grand public : le nom, une ligne d'avantages, l'essai et le prix en
 * bref. Elle annonce le paywall, elle ne le remplace pas : les avantages en
 * détail, le choix de la formule et le paiement restent dans `vw-premium-toast`.
 *
 * L'essai vient de `PremiumService.trialOffer` et le prix de `PricingService`
 * (jamais un montant écrit), comme dans le paywall. Sans prix du fournisseur,
 * le prix disparaît.
 *
 * Usage : `<vw-premium-upsell-card source="profile_card" (ctaClicked)="openPaywall()" />`
 *
 * Premium fermé, la carte ne s'affiche jamais, quel que soit l'écran hôte.
 */
@Component({
  selector: 'vw-premium-upsell-card',
  standalone: true,
  imports: [TranslatePipe, ChevronRightIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './premium-upsell-card.component.html',
})
export class PremiumUpsellCardComponent implements OnInit {
  /** Emplacement, renvoyé avec le toucher (`ctaClicked`). */
  @Input() source = 'unknown';

  @Output() ctaClicked = new EventEmitter<string>();

  readonly premiumService = inject(PremiumService);
  private readonly pricing = inject(PricingService);
  private readonly checkout = inject(CheckoutProviderService);

  ngOnInit(): void {
    // Les prix ne sont chargés qu'une fois pour toute l'app ; sur le web, ils
    // n'arrivaient qu'à l'ouverture du paywall.
    if (!this.checkout.isOutdatedApp) void this.pricing.load();
  }

  /** L'essai offert ici, ou null (`PremiumService.trialOffer`). */
  get trial(): TrialOffer | null {
    return this.premiumService.trialOffer();
  }

  /**
   * Prix court de la formule que le paywall présélectionne (l'annuel, sinon le
   * mensuel) : « 39,99 €/an », ou « puis 39,99 €/an » après un essai. La
   * résiliation et le détail sont sous le bouton du paywall.
   *
   * Ancienne version de l'app : elle ne vend rien, donc aucun prix.
   */
  get priceLine(): { key: string; price: string } | null {
    if (this.checkout.isOutdatedApp) return null;
    const plan = (['YEARLY', 'MONTHLY'] as CheckoutPlan[]).find((p) => this.pricing.hasPrice(p));
    if (!plan) return null;
    const period = plan === 'YEARLY' ? 'yearly' : 'monthly';
    const key = this.premiumService.trialOffer(plan) ? `premium.profile.then-${period}` : `premium.paywall.${period}`;
    return { key, price: this.pricing.label(plan) };
  }

  onClick(): void {
    this.ctaClicked.emit(this.source);
  }
}
